import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { FileUp, LoaderCircle, AlertTriangle, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'
import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import { useUpload } from '../../../components/file-manager/hooks/useUpload'
import { useRouter } from '../../../hooks'
import { MAX_IMPORT_BYTES } from '../lib/constants'
import { describeScheduleError } from '../lib/errors'
import type { ScheduleImportResult } from '../types'

interface Props { open: boolean; onOpenChange: (open: boolean) => void; projectId: string }
type Phase = 'pick' | 'uploading' | 'importing' | 'done'

const validateFile = (file: File, t: (k: string) => string): string | null => {
  const lower = file.name.toLowerCase()
  if (lower.endsWith('.mpp')) return t('Schedule_import_mpp_not_supported')
  if (!lower.endsWith('.xml')) return t('Schedule_import_not_xml')
  if (file.size > MAX_IMPORT_BYTES) return t('Schedule_import_too_large')
  return null
}
const stripExt = (name: string) => name.replace(/\.[^.]+$/, '')
const formatMb = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`

const ScheduleImportDialog: React.FC<Props> = ({ open, onOpenChange, projectId }) => {
  const { t } = useTranslation()
  const { history } = useRouter()
  const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [name, setName] = useState('')
  const [phase, setPhase] = useState<Phase>('pick')
  const [error, setError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [result, setResult] = useState<ScheduleImportResult | null>(null)

  const refetch = [{ query: Query.SCHEDULES, variables: { projectId, includeArchived: false } }, { query: Query.SCHEDULES, variables: { projectId, includeArchived: true } }]
  const [createFile] = useMutation(Mutation.CREATE_FILE)
  const [setFileUploaded] = useMutation(Mutation.SET_FILE_UPLOADED)
  const [deleteFile] = useMutation(Mutation.DELETE_FILE)
  const [importXml] = useMutation(Mutation.IMPORT_SCHEDULE_XML, { refetchQueries: refetch })
  const [upload, { uploads }] = useUpload()

  const busy = phase === 'uploading' || phase === 'importing'
  // useUpload's own `progress` value is never updated during a transfer (only reset to 0 on
  // completion/error) — live per-file progress lives in `uploads`, keyed by signedUrl. This
  // dialog only ever uploads one file at a time, so take whichever single entry is present.
  const uploadProgress = (Object.values(uploads)[0] as { progress?: number } | undefined)?.progress ?? 0

  useEffect(() => {
    if (open) {
      setFile(null)
      setName('')
      setPhase('pick')
      setError(null)
      setDragOver(false)
      setResult(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const pick = (f: File | null) => {
    setError(f ? validateFile(f, t) : null)
    setFile(f)
    if (f && !name) setName(stripExt(f.name))
  }

  const handleImport = async () => {
    if (!file || error) return
    let fileId: string | null = null
    try {
      setPhase('uploading')
      const { data } = await createFile({ variables: { input: { filename: file.name, size: file.size, mimetype: file.type || 'application/xml', encoding: 'utf8', directory: 'schedule' } } })
      fileId = data.createFile.file.id
      const uploaded = await upload([{ file, id: fileId!, name: file.name, signedUrl: data.createFile.uploadUrl }])
      if (!Array.isArray(uploaded)) {
        const orphans = (uploaded as any)?.fileIds || []
        if (orphans.length) await deleteFile({ variables: { ids: orphans } })
        setPhase('pick')
        return
      }
      await setFileUploaded({ variables: { ids: uploaded } })
      setPhase('importing')
      const res = await importXml({ variables: { projectId, fileId, name: name.trim() || null } })
      setResult(res.data.importScheduleXml)
      setPhase('done')
    } catch (e: any) {
      // Review fix (Important #5, spec §2 step 2): a failure past this point (upload succeeded,
      // `setFileUploaded` or `importXml` rejected) leaves the uploaded `File` row + S3 object
      // orphaned unless it's cleaned up here — the pre-upload branch above already does this for
      // an upload failure. The cleanup itself failing is not worth surfacing to the user; they
      // only need to see the import error.
      if (fileId) {
        try { await deleteFile({ variables: { ids: [fileId] } }) } catch (cleanupError) { console.warn('Failed to clean up orphaned schedule import file', cleanupError) }
      }
      setError(describeScheduleError(e.message, t))
      setPhase('pick')
    }
  }

  const close = () => { if (!busy) onOpenChange(false) }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) close() }}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileUp className="h-5 w-5 text-primary" />{t('Schedule_import_title')}</DialogTitle>
        </DialogHeader>

        {phase !== 'done' && (
          <div className="space-y-4">
            <div
              className={cn('rounded-md border-2 border-dashed p-6 text-center cursor-pointer transition-colors', dragOver ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50', busy && 'pointer-events-none opacity-60')}
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); pick(e.dataTransfer.files?.[0] ?? null) }}
            >
              <input ref={inputRef} type="file" accept=".xml,application/xml,text/xml" className="hidden" onChange={(e) => { pick(e.target.files?.[0] ?? null); e.target.value = '' }} />
              {file ? (
                <div className="text-sm"><span className="font-medium">{file.name}</span> <span className="text-muted-foreground">({formatMb(file.size)})</span></div>
              ) : (
                <div className="text-sm text-muted-foreground">{t('Schedule_import_drop_hint')}</div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="schedule-import-name">{t('Schedule_field_name')}</Label>
              <Input id="schedule-import-name" value={name} onChange={(e) => setName(e.target.value)} disabled={busy} placeholder={t('Schedule_import_name_placeholder')} />
            </div>
            {phase === 'uploading' && <Progress value={uploadProgress} />}
            {phase === 'importing' && <div className="flex items-center gap-2 text-sm text-muted-foreground"><LoaderCircle className="h-4 w-4 animate-spin" />{t('Schedule_import_running')}</div>}
            {error && <p className="text-sm text-destructive flex items-start gap-2"><AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />{error}</p>}
            <div className="flex items-center justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={close} disabled={busy}>{t('Common_cancel')}</Button>
              <Button onClick={handleImport} disabled={!file || !!error || busy}>
                {busy && <LoaderCircle className="h-4 w-4 animate-spin" />}
                {t('Schedule_import_action')}
              </Button>
            </div>
          </div>
        )}

        {phase === 'done' && result && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm"><CheckCircle2 className="h-5 w-5 text-success" />{t('Schedule_import_done', { name: result.schedule.name })}</div>
            <div className="grid grid-cols-3 gap-3 text-center">
              {[['Schedule_activities', result.activityCount], ['Schedule_links', result.dependencyCount], ['Schedule_calendars', result.calendarCount]].map(([k, v]) => (
                <div key={k as string} className="rounded-md border p-3"><div className="text-2xl font-bold">{v}</div><div className="text-xs text-muted-foreground">{t(k as string)}</div></div>
              ))}
            </div>
            {result.warnings.length > 0 && (
              <div className="max-h-64 overflow-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left"><tr><th className="p-2">{t('Schedule_import_warning_code')}</th><th className="p-2">{t('Schedule_import_warning_message')}</th><th className="p-2">{t('Schedule_col_name')}</th></tr></thead>
                  <tbody>
                    {result.warnings.map((w, i) => (
                      <tr key={i} className="border-t"><td className="p-2 whitespace-nowrap">{t(`Schedule_warning_${w.code}`, w.code)}</td><td className="p-2">{w.message}</td><td className="p-2">{w.activityName ?? (w.activityExternalUid != null ? `#${w.activityExternalUid}` : '')}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex items-center justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)}>{t('Common_close')}</Button>
              <Button onClick={() => { onOpenChange(false); history.push(`/project/${projectId}/schedule/${result.schedule.id}`) }}>{t('Schedule_open')}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
export default ScheduleImportDialog
